import { Flex, Group, Tabs, Title } from "@mantine/core";
import { IconPlus, IconSettings } from "@tabler/icons-react";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";

import type { RouterContextType } from "@/api/permissions/permissions.api";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
import { AddExpenseFlow } from "@/components/features/AccountsPayable/AddExpense/AddExpenseFlow";
import { AchExportList } from "@/components/features/AccountsPayable/AchExports/AchExportList";
import { PaymentRequestList } from "@/components/features/AccountsPayable/Queue/PaymentRequestList";
import { Button } from "@/components/ui/Button/Button";
import { usePermissions } from "@/hooks/auth/usePermissions";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { useRequestScope } from "@/hooks/useRequestScope";
import { usePersistedTabs } from "@/hooks/usePersistedTabs";
import { tableSearchSchema } from "@/hooks/useTableUrlPersistence";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { getRouteIcon } from "@/utils/getRouteIconComponent";
import { RECORD_TENANT_SEARCH_FIELDS } from "@/utils/recordTenantRoute";

import type { APStatus } from "@/api/accounts-payable/accountsPayable.types";
import type { TabItemType } from "@/types/common.types";

import styles from "./accounts-payable.module.scss";

const accountsPayableSearchSchema = tableSearchSchema.extend({
  tab: z.string().optional().catch(undefined),
  requestId: z.string().optional().catch(undefined),
  ...RECORD_TENANT_SEARCH_FIELDS,
});

export const Route = createFileRoute("/_authenticated/accounts-payable/")({
  component: AccountsPayable,
  validateSearch: accountsPayableSearchSchema.parse,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

const ACH_EXPORTS_TAB = { value: "achExports", label: "ACH Exports" } as const;

const QUEUE_TABS: {
  value: string;
  label: string;
  viewName: string;
  statuses?: APStatus[];
}[] = [
  { value: "all", label: "All", viewName: "apQueueAll" },
  {
    value: "toBePaid",
    label: "To be Paid",
    // SERV-3438: renamed from apQueueToBePaid when the pinned select column was added, so column
    // prefs saved before it do not push that column to the end.
    viewName: "apQueueToBePaidSelectable",
    statuses: ["TO_BE_PAID"],
  },
  { value: "paid", label: "Paid", viewName: "apQueuePaid", statuses: ["PAID"] },
  {
    value: "refundRequests",
    label: "Refund Requests",
    viewName: "apQueueRefundRequests",
    statuses: ["REFUNDED"],
  },
  { value: "failed", label: "Failed", viewName: "apQueueFailed", statuses: ["FAILED"] },
];

function AccountsPayable() {
  const searchParams = useSearch({ from: "/_authenticated/accounts-payable/" });
  const requestScope = useRequestScope();
  const navigate = useNavigate();
  const [isAddExpenseOpen, setIsAddExpenseOpen] = useState(false);

  useBreadcrumb(
    [
      {
        title: "Accounts Payable",
        href: "/accounts-payable",
        icon: getRouteIcon(MODULE_KEYS_MAP.accountsPayable),
      },
    ],
    { listView: "List View" },
  );

  const { hasAccess } = usePermissions();
  const { create: canCreateExpense, edit: canManage } = hasAccess({
    module: MODULE_KEYS_MAP.accountsPayable,
  });

  const { activeTab, setActiveTab } = usePersistedTabs({ defaultTab: "all" });

  const tabsData: TabItemType[] = [
    ...QUEUE_TABS.map((tab) => ({
      value: tab.value,
      label: tab.label,
      component: (
        <PaymentRequestList
          viewName={tab.viewName}
          statuses={tab.statuses}
          isActive={activeTab === tab.value}
          searchParams={searchParams}
          enableBulkPayment={tab.value === "toBePaid"}
        />
      ),
    })),
    {
      ...ACH_EXPORTS_TAB,
      // SERV-3439: files, not payment requests, so it is not one of the status tabs above.
      component: (
        <AchExportList isActive={activeTab === ACH_EXPORTS_TAB.value} searchParams={searchParams} />
      ),
    },
  ];

  return (
    <Flex direction="column" className={styles.container}>
      <Group justify="space-between" align="center" className={styles.header}>
        <Title order={3} className={styles.title}>
          Accounts Payable
        </Title>
        <Group gap="var(--spacing-small)">
          {canManage && (
            <Button
              variant="secondary"
              style={{ height: "var(--component-size-small)" }}
              iconLeft={<IconSettings size={16} />}
              onClick={() => navigate({ to: "/accounts-payable/manage" })}
            >
              Manage
            </Button>
          )}
          {canCreateExpense && (
            <Button
              variant="primary"
              style={{ height: "var(--component-size-small)" }}
              iconLeft={<IconPlus size={16} />}
              onClick={() => setIsAddExpenseOpen(true)}
            >
              Add an Expense
            </Button>
          )}
        </Group>
      </Group>

      <Tabs
        key={requestScope}
        value={activeTab}
        onChange={(value) => setActiveTab(value || "all")}
        className={styles.tab}
      >
        <Tabs.List className={styles.tabsList}>
          {tabsData.map((tab) => (
            <Tabs.Tab key={tab.value} value={tab.value} className={styles.tab}>
              {tab.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        {tabsData.map((tab) => (
          <Tabs.Panel key={tab.value} value={tab.value} className={styles.tabPanel}>
            {tab.component}
          </Tabs.Panel>
        ))}
      </Tabs>

      <AddExpenseFlow isOpen={isAddExpenseOpen} onClose={() => setIsAddExpenseOpen(false)} />
    </Flex>
  );
}
